import { useEffect, useMemo, useState } from "react";
import { Film, Download, Image as ImageIcon, Search } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { CinematicStage, StageHeader, GlassCard, EmptyState, Spinner, cx } from "@/components/shared";

export default function Assets() {
  const [assets, setAssets] = useState(null);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/assets").then(({ data }) => setAssets(data)).catch(() => setAssets([])); }, []);

  const cats = ["All", "Scene", "Thumbnail", "Character"];
  const filtered = useMemo(() => {
    if (!assets) return [];
    let out = assets;
    if (cat !== "All") out = out.filter((a) => a.category === cat);
    if (q) out = out.filter((a) => a.name.toLowerCase().includes(q.toLowerCase()));
    return out;
  }, [assets, cat, q]);

  if (!assets) return <Spinner label="Loading Assets…" />;

  return (
    <CinematicStage>
      <StageHeader stage="POST" room="The Vault · Assets" icon={Film} title="Every generated frame, in one vault"
        subtitle="Scenes, thumbnails and character sheets from all your productions." />

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex items-center gap-2 flex-1 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search assets…" data-testid="asset-search" className="bg-transparent outline-none text-sm w-full text-white placeholder:text-slate-500" />
        </div>
        <div className="flex gap-2">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`asset-cat-${c}`}
              className={cx("px-4 py-2 rounded-xl text-sm border transition", cat === c ? "bg-[#5B4DFF]/25 border-[#5B4DFF]/60 text-white" : "bg-white/5 border-white/10 text-slate-300 hover:text-white")}>{c}</button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Film} title="No assets yet" subtitle="Generate a project and its media will appear here." />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((a, i) => (
            <GlassCard key={i} hover className="overflow-hidden group" data-testid="asset-card">
              <div className="aspect-video bg-[#0A1435] relative">
                {a.url ? <img src={mediaUrl(a.url)} alt={a.name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-slate-600"><ImageIcon className="w-6 h-6" /></div>}
                <a href={mediaUrl(a.url)} download target="_blank" rel="noreferrer" className="absolute top-2 right-2 p-2 rounded-lg bg-black/50 backdrop-blur opacity-0 group-hover:opacity-100 transition text-white" data-testid="asset-download"><Download className="w-4 h-4" /></a>
              </div>
              <div className="p-3">
                <div className="text-xs text-white truncate">{a.name}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{a.category}</div>
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </CinematicStage>
  );
}
